import { Droplets, Wind, SunMedium, Sunrise, Sunset, ThermometerSun, MapPin } from "lucide-react";
import { useWeather, decodeWeather } from "@/lib/useWeather";
import { useCity } from "@/lib/useCity";
import { useI18n } from "@/context/I18nContext";
import { useNow, MO_EN, MO_AR, sunPct, fmt12s, formatTime12, getWeatherAlert } from "@/lib/weatherCardLogic";
import { useWeatherNotifier } from "@/lib/useWeatherNotifier";
import { SecondsTicker } from "@/components/dashboard/neu/SecondsTicker";

function Pill({ icon: I, tone, label, value }) {
  return (
    <div className="neu-inset rounded-2xl px-2 py-2.5 flex flex-col items-center gap-1 flex-1 min-w-0">
      <I className={`w-4 h-4 ${tone} shrink-0`} strokeWidth={2.5} />
      <div className="text-sm font-black text-[#1e3a8a] tabular-nums leading-none">{value}</div>
      <div className="text-[9px] font-bold text-slate-500 leading-none tracking-wide">{label}</div>
    </div>
  );
}

export function NeuTimeCard() {
  const { lang } = useI18n();
  const now = useNow();
  const w = useWeather();
  const place = useCity();
  const { h12, mm, ampm } = formatTime12(now);
  const dd = String(now.getDate()).padStart(2, "0");
  const wd = now.toLocaleDateString(lang === "ar" ? "ar-AE" : "en-US", { weekday: "short" });
  const mo = (lang === "ar" ? MO_AR : MO_EN)[now.getMonth()];
  const info = w ? decodeWeather(w.code) : null;
  const WIcon = info?.Icon;
  const cond = info ? (lang === "ar" ? info.ar : info.en) : "…";
  const L = (en, ar) => (lang === "ar" ? ar : en);
  const pct = sunPct(w?.sunrise, w?.sunset, now);
  const alert = getWeatherAlert(w, lang);
  useWeatherNotifier(alert);

  return (
    <div data-testid="neu-time-card" dir="ltr" className="relative overflow-hidden w-full h-full min-h-[220px] p-4 md:p-5 rounded-[20px] neu flex flex-col justify-between gap-3">
      {alert && (
        <div data-testid="weather-alert" className={`absolute top-0 inset-x-0 ${alert.tone} text-white px-4 py-1.5 text-[11px] font-bold flex items-center gap-2 shadow-md z-20`}>
          <span className="text-sm">{alert.icon}</span>
          <span className="truncate">{alert.text}</span>
        </div>
      )}
      <div className={`flex items-start justify-between gap-3 relative ${alert ? "mt-6" : ""}`}>
        <div dir="ltr" className="relative min-w-0">
          {(place.city || place.country) && (
            <div data-testid="city-country" className="mb-1 text-[10px] font-bold text-slate-500 tracking-wide flex items-center gap-1">
              <MapPin className="w-3 h-3" strokeWidth={2.4} />
              <span className="truncate">{[place.city, place.country].filter(Boolean).join(", ")}</span>
            </div>
          )}
          <div className="flex items-baseline gap-1 relative z-10">
            <span data-testid="hero-time" className="text-4xl md:text-5xl font-black text-[#1e3a8a] tabular-nums tracking-tight leading-none">{h12}:{mm}</span>
            <span className="text-xl md:text-2xl font-black text-slate-400 tabular-nums tracking-tight leading-none">:<SecondsTicker /></span>
            <span className="text-xs font-bold text-slate-500 tracking-widest">{ampm}</span>
          </div>
          <div className="mt-1.5 text-xs font-bold text-slate-500 relative z-10 tracking-wide">
            <span className="uppercase">{wd}</span> · {mo} {dd}
          </div>
        </div>
        <div className="text-end flex-shrink-0">
          <div className="flex items-center justify-end gap-2">
            <div className="w-9 h-9 rounded-2xl grid place-items-center neu-sm shadow-[0_0_18px_rgba(251,191,36,0.25)] shrink-0">
              {WIcon && <WIcon className="w-5 h-5 text-[#c88a06]" strokeWidth={2.2} />}
            </div>
            <div dir="ltr" data-testid="hero-temp" className="text-5xl md:text-6xl font-extralight tracking-tighter text-[#1e3a8a] leading-none tabular-nums">
              {w?.temp ?? "--"}<span className="font-light">°</span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-end">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] md:text-xs font-bold text-[#c88a06] bg-[#fff7e0] border border-[#f8d977] tracking-wide">
              {cond}
            </span>
          </div>
          <div dir="ltr" className="mt-1 text-[10px] font-bold text-slate-500 tabular-nums">
            {L("Feels", "شعور")} {w?.feels ?? "--"}°
          </div>
        </div>
      </div>
      <div dir="ltr" className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-amber-700 tabular-nums flex items-center gap-1 shrink-0"><Sunrise className="w-3 h-3" strokeWidth={2.4} />{fmt12s(w?.sunrise)}</span>
        <div data-testid="sun-track" className="flex-1 h-1.5 rounded-full neu-inset relative">
          {pct >= 0 && (
            <div className="w-3 h-3 rounded-full bg-[#fbbf24] shadow-[0_0_10px_#fbbf24] absolute top-1/2 -translate-y-1/2 -translate-x-1/2" style={{ left: `${pct * 100}%` }} />
          )}
        </div>
        <span className="text-[10px] font-bold text-orange-700 tabular-nums flex items-center gap-1 shrink-0">{fmt12s(w?.sunset)}<Sunset className="w-3 h-3" strokeWidth={2.4} /></span>
      </div>
      <div dir="ltr" className="grid grid-cols-4 gap-2">
        <Pill icon={ThermometerSun} tone="text-red-500" label={L("Hi / Lo", "أعلى/أدنى")} value={`${w?.hi ?? "--"}° / ${w?.lo ?? "--"}°`} />
        <Pill icon={Droplets} tone="text-blue-500" label={L("Humidity", "الرطوبة")} value={`${w?.humidity ?? "--"}%`} />
        <Pill icon={Wind} tone="text-slate-500" label={L("Wind", "الرياح")} value={`${w?.wind ?? "--"}`} />
        <Pill icon={SunMedium} tone="text-orange-500" label={L("UV", "أشعة")} value={`${w?.uv ?? "--"}`} />
      </div>
    </div>
  );
}
